import { userStore } from "../redux/user";
import { wsConnectionStore } from "../redux/wsConnection";
import { reportRussiaWebsocketEvent } from "./clientHealth";
import { routeIncommingMessage } from "./wsInRoutes";

function getLongPollingUrl() {
  return wsConnectionStore.getState().longPollingUrl;
}

let websocketClosedForLongPolling = false;

async function closeWebsocketAfterPollingConnect() {
  if (websocketClosedForLongPolling) {
    return;
  }

  websocketClosedForLongPolling = true;

  try {
    const { disconnectWebSocket } = await import("./websocket");
    disconnectWebSocket("switch to long polling");
  } catch (e) {
    reportRussiaWebsocketEvent("long_polling_websocket_close_failed", {
      message: e?.message,
    });
  }
}

export async function longPollingGetRequest(
  username: string,
  sessionId: string,
  gameId: string
) {
  const longPollingUrl = getLongPollingUrl();
  if (!longPollingUrl) {
    reportRussiaWebsocketEvent("long_polling_missing_url");
    setTimeout(() => {
      longPollingGetRequest(username, sessionId, gameId);
    }, 1000);
    return;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000); // cancel after 10s

  try {
    const params = new URLSearchParams({
      username,
      sessionId,
      gameId: gameId || "",
    });
    const response = await fetch(`${longPollingUrl}/long-polling?${params}`, {
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (!response.ok) {
      reportRussiaWebsocketEvent("long_polling_get_failed", {
        status: response.status,
      });
      setTimeout(() => {
        longPollingGetRequest(username, sessionId, gameId);
      }, 1000);
      return;
    }

    const data = await response.json();

    if (data?.error) {
      reportRussiaWebsocketEvent("long_polling_server_error", {
        cmd: data.cmd,
      });
    } else {
      void closeWebsocketAfterPollingConnect();
    }

    if (data?.cmd && data.cmd != "NO_DATA") {
      reportRussiaWebsocketEvent("long_polling_message", {
        cmd: data.cmd,
      });
      routeIncommingMessage(data);
    }

    import("../redux/gamePlay").then(async ({ gamePlayStore }) => {
      const { gameId } = gamePlayStore.getState();
      longPollingGetRequest(username, sessionId, gameId);
    });

    if (data?.cmd == "NO_DATA") {
      setTimeout(() => {
        import("../redux/gamePlay").then(({ gamePlayStore }) => {
          const { gameId, turns, moves } = gamePlayStore.getState();
          import("./wsOutRoutes").then(({ GamePlayWs }) => {
            GamePlayWs.sendGameSync(gameId, turns, moves);
          });
        });
      }, 200);
    }
  } catch (e) {
    clearTimeout(timeout);
    if (e.name === "AbortError") {
      console.warn("Request timed out. Retrying...");
      reportRussiaWebsocketEvent("long_polling_get_timeout");
    } else {
      console.error("Fetch error", e);
      reportRussiaWebsocketEvent("long_polling_get_error", {
        message: e?.message,
      });
    }

    // Retry immediately if it failed or timed out
    setTimeout(() => {
      longPollingGetRequest(username, sessionId, gameId);
    }, 1000);
  }
}

export async function longPollingPostRequest(message: object) {
  const username = userStore.getState().username;
  const sessionId = sessionStorage.getItem("sessionId");
  const longPollingUrl = getLongPollingUrl();

  if (!longPollingUrl || !username || !sessionId) {
    reportRussiaWebsocketEvent("long_polling_post_skipped", {
      hasUrl: !!longPollingUrl,
      hasUsername: !!username,
      hasSessionId: !!sessionId,
    });
    return;
  }

  try {
    const response = await fetch(`${longPollingUrl}/command`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username,
        sessionId,
        message,
      }),
    });

    if (!response.ok) {
      reportRussiaWebsocketEvent("long_polling_post_failed", {
        status: response.status,
        cmd: (message as any)?.cmd,
      });
      return;
    }
  } catch (e) {
    console.log(e);
    reportRussiaWebsocketEvent("long_polling_post_error", {
      message: e?.message,
      cmd: (message as any)?.cmd,
    });
  }
}
